import { Box, Typography } from '@mui/material';
import React, { useMemo } from 'react';
import Colors from '../../../utils/color';
import { ParsedModifierGroup } from '../../../types/menu';

/**
 * The heading's own name — what a customer would recognise — without the
 * machine decorations this component paints around it: the `[parent]` prefix,
 * the `!` unmet-minimum marker, and the `(select …)` counter.
 *
 * Exported so the Alt+click read-out (HITL-1532) speaks exactly the name that
 * is painted and cannot drift from it.
 */
export const getModGroupDisplayName = (modGroup: ParsedModifierGroup) =>
  modGroup.uniqueName || modGroup.name;

export function ModGroupLabel({
  modGroup,
  selectedModNumber,
  parentModifier,
}: {
  modGroup: ParsedModifierGroup;
  selectedModNumber: number;
  parentModifier: string;
}) {
  const isFree = modGroup.maximumSelections <= 0;
  const isSingleChoice =
    modGroup.minimumSelections === modGroup.maximumSelections;
  const isAtLeastOneRequired = modGroup.minimumSelections > 0;
  const isRequiredMinimumNotMet =
    isAtLeastOneRequired && modGroup.minimumSelections > selectedModNumber;
  const optionsCount = Object.values(modGroup.menuItems).length;
  const max =
    modGroup.maximumSelections !== -1
      ? Math.min(modGroup.maximumSelections, optionsCount)
      : optionsCount;

  const subText = useMemo(() => {
    if (isFree) return '';
    if (isSingleChoice) return `(select ${modGroup.minimumSelections})`;
    if (isAtLeastOneRequired)
      return `(select between ${modGroup.minimumSelections} and ${max})`;
    return `(select up to ${max})`;
  }, []);

  const modGroupText = getModGroupDisplayName(modGroup) + ' ' + subText;

  return (
    <Typography component="div" color="textPrimary">
      {parentModifier && (
        <Box
          sx={{
            typography: 'subtitle2',
            fontWeight: 'bold',
            fontSize: '14px',
            display: 'inline',
            marginInlineEnd: '5px',
          }}
        >{`[${parentModifier}]`}</Box>
      )}
      <Box
        sx={{
          typography: 'subtitle2',
          fontSize: '14px',
          display: 'inline',
          color: isRequiredMinimumNotMet ? Colors.Thunderbird : '',
        }}
      >
        {isRequiredMinimumNotMet ? `! ${modGroupText}` : modGroupText}
      </Box>
    </Typography>
  );
}
